<template>
  <div>
    <v-alert
      v-if="statusAlert"
      :icon="statusAlert.icon"
      prominent
      text
      :type="statusAlert.type"
      class="mb-4"
    >
      <p class="font-weight-semibold mb-1">
        {{ statusAlert.title }}
      </p>
      <span>
        {{ statusAlert.message }}
      </span>
    </v-alert>

    <v-card v-if="showForm">
      <v-card-text class="pt-5">
        <p class="text-h6 mb-1">
          Solicitação de Homologação RCS
        </p>
        <p class="text--secondary mb-0">
          {{ formIntro }}
          Campos com <strong>*</strong> são obrigatórios.
        </p>
      </v-card-text>

      <v-divider />

      <validation-observer
        ref="observer"
        autocomplete="off"
        tag="form"
      >
        <v-card-text class="pt-6">
          <v-row>
            <v-col
              cols="12"
              lg="8"
            >
              <!-- Categoria RCS -->
              <p class="text-subtitle-1 font-weight-semibold mb-0">
                Categoria RCS *
              </p>
              <div class="d-flex flex-wrap align-center">
                <v-checkbox
                  v-model="form.nonConversational"
                  label="Não Conversacional"
                  class="me-6 mt-0"
                  hide-details
                  dense
                />
                <v-checkbox
                  v-model="form.conversational"
                  label="Conversacional"
                  class="mt-0"
                  hide-details
                  dense
                />
              </div>
              <p
                v-if="!hasCategory"
                class="error--text text-caption mt-1 mb-0"
              >
                Selecione ao menos uma categoria.
              </p>

              <v-row
                v-if="form.nonConversational"
                class="mt-3"
              >
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome do agente Não Conversacional"
                    rules="required|max:100"
                    vid="nonConversationalAgentName"
                  >
                    <v-text-field
                      v-model="form.nonConversationalAgentName"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      :counter="100"
                      label="Nome do agente Não Conversacional *"
                      outlined
                      dense
                      @input="form.nonConversationalCode = toAgentCode($event)"
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Código Não Conversacional"
                    rules="max:100"
                    vid="nonConversationalCode"
                  >
                    <v-text-field
                      v-model="form.nonConversationalCode"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Código Não Conversacional"
                      outlined
                      dense
                      disabled
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Caso de uso Não Conversacional"
                    rules="required"
                    vid="nonConversationalUseCase"
                  >
                    <v-select
                      v-model="form.nonConversationalUseCase"
                      :items="useCases"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Caso de uso Não Conversacional *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row v-if="form.conversational">
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome do agente Conversacional"
                    rules="required|max:100"
                    vid="conversationalAgentName"
                  >
                    <v-text-field
                      v-model="form.conversationalAgentName"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      :counter="100"
                      label="Nome do agente Conversacional *"
                      outlined
                      dense
                      @input="form.conversationalCode = toConversationalAgentCode($event)"
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Código Conversacional"
                    rules="max:100"
                    vid="conversationalCode"
                  >
                    <v-text-field
                      v-model="form.conversationalCode"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Código Conversacional"
                      outlined
                      dense
                      disabled
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Caso de uso Conversacional"
                    rules="required"
                    vid="conversationalUseCase"
                  >
                    <v-select
                      v-model="form.conversationalUseCase"
                      :items="useCases"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Caso de uso Conversacional *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <!-- Configuracao -->
              <p class="text-subtitle-1 font-weight-semibold mt-6 mb-2">
                Configuração
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Razão social"
                    rules="required|min:2|max:120"
                    vid="brand"
                  >
                    <v-text-field
                      v-model="form.brand"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Razão social *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="CNPJ"
                    rules="required|cnpj_digits"
                    vid="cnpj"
                  >
                    <v-text-field
                      v-model="form.cnpj"
                      v-mask="'##.###.###/####-##'"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="CNPJ *"
                      placeholder="00.000.000/0000-00"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Site da marca"
                    rules="url"
                    vid="brandSite"
                  >
                    <v-text-field
                      v-model="form.brandSite"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Site da marca"
                      placeholder="https://"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <!-- Dados do agente -->
              <p class="text-subtitle-1 font-weight-semibold mt-6 mb-2">
                Dados do agente
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome da empresa"
                    rules="required|min:2|max:160"
                    vid="companyName"
                  >
                    <v-text-field
                      v-model="form.companyName"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Nome da empresa *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Descrição"
                    rules="required"
                    vid="description"
                  >
                    <v-textarea
                      v-model="form.description"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Descrição *"
                      rows="3"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Logo"
                    rules="required"
                    vid="logo"
                  >
                    <v-file-input
                      v-model="form.logo"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      :prepend-inner-icon="icons.mdiCloudUploadOutline"
                      accept="image/png,image/jpeg"
                      label="Logo (224 × 224 px, máx. 1 MB) *"
                      prepend-icon=""
                      outlined
                      dense
                      @change="onPickLogo"
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Banner"
                    rules="required"
                    vid="banner"
                  >
                    <v-file-input
                      v-model="form.banner"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      :prepend-inner-icon="icons.mdiCloudUploadOutline"
                      accept="image/png,image/jpeg"
                      label="Banner (1440 × 448 px, máx. 2 MB) *"
                      prepend-icon=""
                      outlined
                      dense
                      @change="onPickBanner"
                    />
                  </validation-provider>
                </v-col>

                <v-col cols="12">
                  <div class="generator pa-4">
                    <p class="text-subtitle-2 mb-1">
                      Não tem no formato certo? Gere automático (opcional)
                    </p>
                    <p class="text-caption text--secondary mb-3">
                      Suba sua logo em qualquer tamanho/formato. A gente gera opções no padrão
                      224 × 224 e cria um banner com as cores dela.
                    </p>

                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-file-input
                          v-model="rawLogo"
                          :prepend-inner-icon="icons.mdiCloudUploadOutline"
                          accept="image/*"
                          label="Sua logo (qualquer formato)"
                          prepend-icon=""
                          outlined
                          dense
                          hide-details
                          @change="onPickRawLogo"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                        class="d-flex align-center"
                      >
                        <v-btn
                          color="primary"
                          :disabled="!rawLogoDataUrl"
                          @click="generateLogoOptions"
                        >
                          Gerar 3 opções de logo
                        </v-btn>
                      </v-col>
                    </v-row>

                    <template v-if="logoOptions.length">
                      <p class="text-caption mt-2 mb-1">
                        Clique pra escolher a versão da logo (224 × 224):
                      </p>
                      <div class="d-flex flex-wrap">
                        <div
                          v-for="(option, index) in logoOptions"
                          :key="index"
                          class="logo-option me-3"
                          :class="{ 'logo-option--active': selectedLogoOption === index }"
                          @click="useLogoOption(index)"
                        >
                          <img
                            :src="option"
                            alt=""
                          />
                        </div>
                      </div>
                    </template>

                    <v-divider class="my-4" />

                    <p class="text-subtitle-2 mb-2">
                      Banner
                    </p>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          v-model="bannerPhrase"
                          label="Frase do banner (opcional)"
                          hint="A frase fica no topo, longe da logo."
                          persistent-hint
                          outlined
                          dense
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="3"
                      >
                        <v-select
                          v-model="bannerBg"
                          :items="bannerBackgrounds"
                          label="Fundo do banner"
                          outlined
                          dense
                          hide-details
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="3"
                        class="d-flex align-center"
                      >
                        <v-btn
                          color="secondary"
                          outlined
                          @click="generateBanner"
                        >
                          Gerar banner
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="3"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Cor"
                    rules="required|hex_color"
                    vid="color"
                  >
                    <v-text-field
                      v-model="form.color"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Cor *"
                      placeholder="#020202"
                      outlined
                      dense
                    >
                      <template v-slot:prepend-inner>
                        <input
                          v-model="form.color"
                          type="color"
                          class="color-picker"
                        />
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="9"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Mensagem predefinida"
                    rules="required|max:255"
                    vid="presetMessage"
                  >
                    <v-text-field
                      v-model="form.presetMessage"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Mensagem predefinida *"
                      hint="Aparece ao tocar no botão Conversar. Veja o resultado no celular ao lado."
                      persistent-hint
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <p class="text-subtitle-2 mt-4 mb-0">
                Selecione os outros canais que utiliza como alternativa ao RCS *
              </p>
              <div class="d-flex flex-wrap align-center">
                <v-checkbox
                  v-for="channel in alternativeChannels"
                  :key="channel"
                  v-model="form.alternativeChannels"
                  :label="channel"
                  :value="channel"
                  class="me-6 mt-0"
                  hide-details
                  dense
                />
              </div>
              <p
                v-if="!form.alternativeChannels.length"
                class="error--text text-caption mt-1 mb-0"
              >
                Selecione ao menos um canal alternativo.
              </p>

              <!-- Contato do agente -->
              <p class="text-subtitle-1 font-weight-semibold mt-6 mb-2">
                Contato do agente
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="2"
                >
                  <v-select
                    v-model="form.ddi"
                    :items="ddis"
                    label="DDI"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Telefone"
                    :rules="{ required: true, phone_digits: { ddi: form.ddi } }"
                    vid="phone"
                  >
                    <v-text-field
                      v-model="form.phone"
                      v-mask="['(##) ####-####', '(##) #####-####']"
                      :error-messages="errors"
                      :hide-details="errors.length ? false : 'auto'"
                      label="Telefone *"
                      hint="Inclua o DDD"
                      persistent-hint
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="E-mail"
                    rules="required|email"
                    vid="agentEmail"
                  >
                    <v-text-field
                      v-model="form.agentEmail"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="E-mail *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Site oficial da marca"
                    rules="url"
                    vid="officialSite"
                  >
                    <v-text-field
                      v-model="form.officialSite"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Site oficial da marca"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <!-- Privacidade e termos -->
              <p class="text-subtitle-1 font-weight-semibold mt-6 mb-2">
                Privacidade e termos de uso
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="URL Política de privacidade"
                    rules="required|url"
                    vid="privacyUrl"
                  >
                    <v-text-field
                      v-model="form.privacyUrl"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="URL Política de privacidade *"
                      placeholder="https://"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="URL Termos de serviço"
                    rules="required|url"
                    vid="termsUrl"
                  >
                    <v-text-field
                      v-model="form.termsUrl"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="URL Termos de serviço *"
                      placeholder="https://"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Opt-in"
                    rules="required"
                    vid="optin"
                  >
                    <v-select
                      v-model="form.optin"
                      :items="optins"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Opt-in — como o consentimento é obtido? *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Descrição do opt-in"
                    rules="required"
                    vid="optinDescription"
                  >
                    <v-textarea
                      v-model="form.optinDescription"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Descreva como o opt-in é obtido *"
                      rows="3"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <!-- Responsavel -->
              <p class="text-subtitle-1 font-weight-semibold mt-6 mb-2">
                Contato do responsável para validação dos dados
              </p>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome do responsável"
                    rules="required|max:160"
                    vid="responsibleName"
                  >
                    <v-text-field
                      v-model="form.responsibleName"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Nome do responsável *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="E-mail do responsável"
                    rules="required|email"
                    vid="responsibleEmail"
                  >
                    <v-text-field
                      v-model="form.responsibleEmail"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="E-mail do responsável *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Cargo do responsável"
                    rules="required|max:120"
                    vid="responsibleRole"
                  >
                    <v-text-field
                      v-model="form.responsibleRole"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      label="Cargo do responsável *"
                      outlined
                      dense
                    />
                  </validation-provider>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="Cartão CNPJ"
                    rules="required"
                    vid="cnpjCard"
                  >
                    <v-file-input
                      v-model="form.cnpjCard"
                      :error-messages="errors"
                      :hide-details="errors.length === 0"
                      :prepend-inner-icon="icons.mdiCloudUploadOutline"
                      accept="application/pdf"
                      label="Cartão CNPJ (somente PDF, máx. 500 kb) *"
                      prepend-icon=""
                      outlined
                      dense
                      @change="onPickCnpjCard"
                    />
                  </validation-provider>
                </v-col>
              </v-row>

              <v-checkbox
                v-model="acceptCharges"
                class="mt-4"
                hide-details
                dense
              >
                <template v-slot:label>
                  <span>
                    Concordo que, ao criar este canal, estarei sujeito a cobranças extras
                    conforme descrito em contrato. *
                  </span>
                </template>
              </v-checkbox>
              <p
                v-if="!acceptCharges"
                class="error--text text-caption mt-1 mb-0"
              >
                É necessário concordar com as cobranças extras.
              </p>
            </v-col>

            <!-- Preview -->
            <v-col
              cols="12"
              lg="4"
            >
              <div class="phone-preview mx-auto">
                <div class="phone-preview__banner">
                  <img
                    v-if="bannerPreview"
                    :src="bannerPreview"
                    alt=""
                  />
                  <div class="phone-preview__logo">
                    <img
                      v-if="logoPreview"
                      :src="logoPreview"
                      alt=""
                    />
                  </div>
                </div>

                <div class="text-center px-4 pt-6">
                  <p class="font-weight-semibold mb-1">
                    {{ form.companyName || 'Nome da empresa' }}
                  </p>
                  <p class="text-caption phone-preview__muted mb-0">
                    {{ form.description || 'Descrição' }}
                  </p>
                </div>

                <div class="d-flex justify-space-around px-2 py-3">
                  <div
                    v-for="action in previewActions"
                    :key="action.label"
                    class="text-center"
                  >
                    <v-icon
                      :color="action.accent ? previewAccent : undefined"
                      :class="{ 'phone-preview__icon--muted': !action.accent }"
                      size="20"
                    >
                      {{ action.icon }}
                    </v-icon>
                    <p class="text-caption mb-0">
                      {{ action.label }}
                    </p>
                  </div>
                </div>

                <v-divider />

                <div class="px-4 py-2">
                  <div class="d-flex align-center py-2">
                    <v-icon
                      size="18"
                      class="me-3 phone-preview__icon--muted"
                    >
                      {{ icons.mdiPhoneOutline }}
                    </v-icon>
                    <span class="text-caption">{{ previewPhone }}</span>
                  </div>
                  <div class="d-flex align-center py-2">
                    <v-icon
                      :color="previewAccent"
                      size="18"
                      class="me-3"
                    >
                      {{ icons.mdiWeb }}
                    </v-icon>
                    <span class="text-caption">{{ form.officialSite || form.brandSite || 'Site' }}</span>
                  </div>
                  <div class="d-flex align-center py-2">
                    <v-icon
                      size="18"
                      class="me-3 phone-preview__icon--muted"
                    >
                      {{ icons.mdiEmailOutline }}
                    </v-icon>
                    <span class="text-caption">{{ form.agentEmail || 'E-mail' }}</span>
                  </div>

                  <p class="text-caption phone-preview__muted mt-3 mb-1">
                    Ao tocar em "Conversar":
                  </p>
                  <div class="phone-preview__bubble">
                    {{ form.presetMessage || 'Olá, gostaria de mais informações.' }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-text class="pt-6">
          <v-btn
            color="primary"
            class="me-3"
            @click="submit"
          >
            Enviar solicitação
          </v-btn>
          <v-btn
            color="secondary"
            outlined
            @click="resetForm"
          >
            Limpar
          </v-btn>
        </v-card-text>
      </validation-observer>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { extend } from 'vee-validate'
import {
  mdiCloudUploadOutline,
  mdiClockOutline,
  mdiPhoneOutline,
  mdiWeb,
  mdiEmailOutline,
  mdiMessageOutline,
  mdiCloseCircleOutline,
} from '@mdi/js'

const STATUS_PENDING = 1
const STATUS_IN_PROGRESS = 2
const STATUS_REFUSED = 4

const MAX_LOGO_MB = 1
const MAX_BANNER_MB = 2
const MAX_CNPJ_CARD_KB = 500
const BYTES_PER_MB = 1024 * 1024
const BYTES_PER_KB = 1024

const LOGO_SIZE = 224
const LOGO_INNER_SIZE = 200
const BANNER_WIDTH = 1440
const BANNER_HEIGHT = 448
const BANNER_LOGO_SIZE = 180

const HEX_COLOR = /^#([0-9a-fA-F]{6})$/

const digits = value => String(value || '').replace(/\D/g, '')

extend('hex_color', {
  validate: value => HEX_COLOR.test(String(value || '').trim()),
  message: 'Use uma cor hexadecimal, ex.: #020202',
})

extend('cnpj_digits', {
  validate: value => digits(value).length === 14,
  message: 'Informe um CNPJ válido (14 dígitos)',
})

extend('phone_digits', {
  // Brasil (+55): exige DDD (2) + numero (8 ou 9) => 10 ou 11 digitos.
  validate: (value, { ddi } = {}) => {
    const phone = digits(value)
    const isBrazil = !ddi || String(ddi).replace(/\D/g, '') === '55'

    if (isBrazil) return phone.length === 10 || phone.length === 11

    return phone.length >= 8
  },
  params: ['ddi'],
  message: 'Informe o telefone com DDD (ex.: 11999999999)',
})

const toAgentCode = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, '-')

const toConversationalAgentCode = value => {
  const code = toAgentCode(value)

  return code ? `${code}-conversacional` : ''
}

const emptyForm = () => ({
  nonConversational: true,
  nonConversationalAgentName: '',
  nonConversationalCode: '',
  nonConversationalUseCase: '',
  conversational: true,
  conversationalAgentName: '',
  conversationalCode: '',
  conversationalUseCase: '',
  brand: '',
  cnpj: '',
  brandSite: '',
  companyName: '',
  description: '',
  logo: null,
  banner: null,
  cnpjCard: null,
  color: '#1D4ED8',
  presetMessage: 'Olá, gostaria de mais informações.',
  alternativeChannels: ['Telefone'],
  ddi: '+55',
  phone: '',
  agentEmail: '',
  officialSite: '',
  privacyUrl: '',
  termsUrl: '',
  optin: '',
  optinDescription: '',
  responsibleName: '',
  responsibleEmail: '',
  responsibleRole: '',
})

export default {
  props: {
    // Solicitacao existente ainda nao aprovada (pendente, em andamento ou recusada)
    pendingRequest: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      form: emptyForm(),
      acceptCharges: false,
      createdId: null,
      logoPreview: null,
      bannerPreview: null,
      rawLogo: null,
      rawLogoDataUrl: null,
      logoOptions: [],
      selectedLogoOption: null,
      bannerPhrase: '',
      bannerBg: 'solid',
      bannerBackgrounds: [
        { text: 'Cor sólida', value: 'solid' },
        { text: 'Cores da logo', value: 'logo' },
      ],
      useCases: ['Promocional', 'Transacional', 'OTP'],
      alternativeChannels: ['E-mail', 'Telefone', 'Bate-papo online'],
      ddis: ['+55', '+1', '+351', '+34', '+44', '+49', '+33', '+39', '+54', '+56', '+57', '+52', '+598'],
      optins: [
        'Página de captura (formulário com aceite)',
        'Cadastro no site ou app',
        'No momento da compra (checkout)',
        'Base própria / carteira de clientes',
        'Ponto de venda / atendimento presencial',
        'Opt-in em outro canal (SMS, WhatsApp, e-mail)',
        'Outro (especificar)',
      ],
      icons: {
        mdiCloudUploadOutline,
        mdiClockOutline,
        mdiCloseCircleOutline,
        mdiPhoneOutline,
        mdiWeb,
        mdiEmailOutline,
        mdiMessageOutline,
      },
    }
  },

  computed: {
    activeRequest() {
      if (this.createdId) {
        return { id: this.createdId, status: STATUS_PENDING }
      }

      return this.pendingRequest
    },

    activeStatus() {
      return Number(this.activeRequest && this.activeRequest.status)
    },

    isAwaitingApproval() {
      return [STATUS_PENDING, STATUS_IN_PROGRESS].includes(this.activeStatus)
    },

    isRefused() {
      return this.activeStatus === STATUS_REFUSED
    },

    // Em analise: so o alerta. Sem solicitacao ou recusada: formulario aberto.
    showForm() {
      return !this.isAwaitingApproval
    },

    formIntro() {
      if (this.isRefused) {
        return 'Sua solicitação anterior foi recusada. Preencha os dados abaixo para enviar uma nova homologação.'
      }

      return 'Você ainda não possui um agente RCS. Preencha os dados abaixo para solicitar a homologação.'
    },

    statusAlert() {
      if (this.isAwaitingApproval) {
        return {
          type: 'warning',
          icon: this.icons.mdiClockOutline,
          title: `Solicitação #${this.activeRequest.id} em análise`,
          message: 'Sua solicitação está em análise pela LigData. Assim que o agente for aprovado, a tela de disparo de RCS ficará disponível aqui.',
        }
      }

      if (this.isRefused) {
        return {
          type: 'error',
          icon: this.icons.mdiCloseCircleOutline,
          title: `Solicitação #${this.activeRequest.id} recusada`,
          message: 'Sua solicitação anterior foi recusada. Envie uma nova solicitação com os dados corrigidos.',
        }
      }

      return null
    },

    hasCategory() {
      return this.form.nonConversational || this.form.conversational
    },

    previewAccent() {
      return HEX_COLOR.test(this.form.color) ? this.form.color : 'primary'
    },

    // Canvas nao aceita nome de cor do tema, precisa de um hex valido
    safeColor() {
      return HEX_COLOR.test(this.form.color) ? this.form.color : '#0F172A'
    },

    previewPhone() {
      return this.form.phone ? `${this.form.ddi} ${this.form.phone}` : '+55 Contato'
    },

    previewActions() {
      return [
        { label: 'Conversar', icon: this.icons.mdiMessageOutline, accent: false },
        { label: 'Ligar', icon: this.icons.mdiPhoneOutline, accent: false },
        { label: 'Site', icon: this.icons.mdiWeb, accent: true },
        { label: 'E-mail', icon: this.icons.mdiEmailOutline, accent: false },
      ]
    },
  },

  beforeDestroy() {
    this.revokePreviews()
  },

  methods: {
    ...mapActions('rcsHomologation', {
      createRequest: 'create',
    }),

    toAgentCode,
    toConversationalAgentCode,

    onPickLogo(file) {
      if (!this.validateSizeMb(file, MAX_LOGO_MB)) {
        this.form.logo = null
        this.setPreview('logoPreview', null)

        return
      }

      this.setPreview('logoPreview', file)
    },

    onPickBanner(file) {
      if (!this.validateSizeMb(file, MAX_BANNER_MB)) {
        this.form.banner = null
        this.setPreview('bannerPreview', null)

        return
      }

      this.setPreview('bannerPreview', file)
    },

    onPickCnpjCard(file) {
      if (!this.validateSizeKb(file, MAX_CNPJ_CARD_KB)) this.form.cnpjCard = null
    },

    validateSizeMb(file, maxMb) {
      if (!file) return false

      if (file.size > maxMb * BYTES_PER_MB) {
        this.$notify(`Arquivo acima de ${maxMb} MB.`, 'error')

        return false
      }

      return true
    },

    validateSizeKb(file, maxKb) {
      if (!file) return false

      if (file.size > maxKb * BYTES_PER_KB) {
        this.$notify(`Arquivo acima de ${maxKb} kb.`, 'error')

        return false
      }

      return true
    },

    setPreview(key, file) {
      if (this[key]) URL.revokeObjectURL(this[key])

      this[key] = file ? URL.createObjectURL(file) : null
    },

    revokePreviews() {
      if (this.logoPreview) URL.revokeObjectURL(this.logoPreview)
      if (this.bannerPreview) URL.revokeObjectURL(this.bannerPreview)
    },

    async onPickRawLogo(file) {
      this.logoOptions = []
      this.selectedLogoOption = null
      this.rawLogoDataUrl = file ? await this.readAsDataUrl(file) : null
    },

    async generateLogoOptions() {
      if (!this.rawLogoDataUrl) return

      try {
        this.logoOptions = await Promise.all([
          this.makeLogoVariant('cover'),
          this.makeLogoVariant('contain-white'),
          this.makeLogoVariant('contain-color'),
        ])
        this.selectedLogoOption = null
      } catch (err) {
        this.$notify('Não foi possível processar essa imagem.', 'error')
      }
    },

    // Gera a logo no padrao 224x224 exigido pelo RCS: preenchendo todo o quadro
    // (cover) ou centralizada sobre fundo branco/cor da marca (contain).
    async makeLogoVariant(mode) {
      const image = await this.loadImage(this.rawLogoDataUrl)
      const canvas = document.createElement('canvas')
      canvas.width = LOGO_SIZE
      canvas.height = LOGO_SIZE

      const ctx = canvas.getContext('2d')
      ctx.fillStyle = mode === 'contain-color' ? this.safeColor : '#ffffff'
      ctx.fillRect(0, 0, LOGO_SIZE, LOGO_SIZE)

      const scale = mode === 'cover'
        ? Math.max(LOGO_SIZE / image.width, LOGO_SIZE / image.height)
        : Math.min(LOGO_INNER_SIZE / image.width, LOGO_INNER_SIZE / image.height)

      const width = image.width * scale
      const height = image.height * scale

      ctx.drawImage(image, (LOGO_SIZE - width) / 2, (LOGO_SIZE - height) / 2, width, height)

      return canvas.toDataURL('image/png')
    },

    useLogoOption(index) {
      const file = this.dataUrlToFile(
        this.logoOptions[index],
        `logo-opcao-${index + 1}.png`,
        'image/png',
      )

      this.selectedLogoOption = index
      this.form.logo = file
      this.setPreview('logoPreview', file)

      if (file.size > MAX_LOGO_MB * BYTES_PER_MB) {
        this.$notify(
          `A logo gerada ficou com ${(file.size / BYTES_PER_MB).toFixed(2)} MB, acima do limite de ${MAX_LOGO_MB} MB.`,
          'warning',
        )
      }
    },

    async generateBanner() {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = BANNER_WIDTH
        canvas.height = BANNER_HEIGHT

        const ctx = canvas.getContext('2d')
        ctx.fillStyle = await this.resolveBannerBackground()
        ctx.fillRect(0, 0, BANNER_WIDTH, BANNER_HEIGHT)

        if (this.bannerPhrase.trim()) {
          ctx.fillStyle = '#ffffff'
          ctx.font = '600 36px Inter, system-ui, sans-serif'
          ctx.fillText(this.bannerPhrase.trim().slice(0, 48), 64, 72)
        }

        const logoSource = this.logoPreview || this.rawLogoDataUrl

        if (logoSource) {
          const image = await this.loadImage(logoSource)
          ctx.drawImage(
            image,
            BANNER_WIDTH - BANNER_LOGO_SIZE - 80,
            (BANNER_HEIGHT - BANNER_LOGO_SIZE) / 2,
            BANNER_LOGO_SIZE,
            BANNER_LOGO_SIZE,
          )
        }

        const file = this.dataUrlToFile(
          canvas.toDataURL('image/jpeg', 0.86),
          'banner-gerado.jpg',
          'image/jpeg',
        )

        this.form.banner = file
        this.setPreview('bannerPreview', file)
        this.$notify('Banner gerado.', 'success')
      } catch (err) {
        this.$notify('Não foi possível gerar o banner.', 'error')
      }
    },

    async resolveBannerBackground() {
      if (this.bannerBg !== 'logo' || !this.rawLogoDataUrl) return this.safeColor

      const image = await this.loadImage(this.rawLogoDataUrl)

      return this.dominantColor(image)
    },

    // Cor predominante da logo, ignorando transparencia e o branco de fundo
    dominantColor(image) {
      const sample = 48
      const canvas = document.createElement('canvas')
      canvas.width = sample
      canvas.height = sample

      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0, sample, sample)

      const { data } = ctx.getImageData(0, 0, sample, sample)
      const buckets = {}
      let winner = null

      for (let i = 0; i < data.length; i += 4) {
        const [red, green, blue, alpha] = [data[i], data[i + 1], data[i + 2], data[i + 3]]
        const isTransparent = alpha < 128
        const isNearWhite = red > 235 && green > 235 && blue > 235

        if (!isTransparent && !isNearWhite) {
          const key = `${red >> 4}-${green >> 4}-${blue >> 4}`

          if (!buckets[key]) buckets[key] = { count: 0, red: 0, green: 0, blue: 0 }

          const bucket = buckets[key]
          bucket.count += 1
          bucket.red += red
          bucket.green += green
          bucket.blue += blue

          if (!winner || bucket.count > winner.count) winner = bucket
        }
      }

      if (!winner) return this.safeColor

      const channel = total => Math.round(total / winner.count).toString(16).padStart(2, '0')

      return `#${channel(winner.red)}${channel(winner.green)}${channel(winner.blue)}`
    },

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('Imagem inválida.'))
        image.src = src
      })
    },

    readAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'))
        reader.readAsDataURL(file)
      })
    },

    dataUrlToFile(dataUrl, name, type) {
      const binary = atob(dataUrl.split(',')[1])
      const bytes = new Uint8Array(binary.length)

      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)

      return new File([bytes], name, { type })
    },

    async submit() {
      const valid = await this.$refs.observer.validate()

      if (!valid || !this.hasCategory || !this.form.alternativeChannels.length || !this.acceptCharges) {
        this.$notify('Revise os campos obrigatórios antes de enviar.', 'warning')

        return
      }

      const data = await this.createRequest(this.buildPayload())

      if (!data || !data.id) return

      this.createdId = data.id
      this.$notify('Solicitação enviada. Acompanhe o status nesta tela.', 'success')
    },

    buildPayload() {
      const { form } = this

      return {
        partnerName: form.brand.trim() || form.companyName.trim(),
        brand: form.brand.trim(),
        cnpj: digits(form.cnpj),
        brandSite: form.brandSite.trim(),
        nonConversational: form.nonConversational,
        nonConversationalAgentName: form.nonConversational ? form.nonConversationalAgentName.trim() : '',
        nonConversationalCode: form.nonConversational ? form.nonConversationalCode.trim() : '',
        nonConversationalUseCase: form.nonConversational ? form.nonConversationalUseCase : '',
        conversational: form.conversational,
        conversationalAgentName: form.conversational ? form.conversationalAgentName.trim() : '',
        conversationalCode: form.conversational ? form.conversationalCode.trim() : '',
        conversationalUseCase: form.conversational ? form.conversationalUseCase : '',
        companyName: form.companyName.trim(),
        description: form.description.trim(),
        color: form.color.trim(),
        presetMessage: form.presetMessage.trim(),
        alternativeChannels: form.alternativeChannels.join(', '),
        ddi: form.ddi,
        phone: digits(form.phone),
        agentEmail: form.agentEmail.trim(),
        officialSite: form.officialSite.trim(),
        privacyUrl: form.privacyUrl.trim(),
        termsUrl: form.termsUrl.trim(),
        optin: form.optin,
        optinDescription: form.optinDescription.trim(),
        responsibleName: form.responsibleName.trim(),
        responsibleEmail: form.responsibleEmail.trim(),
        responsibleRole: form.responsibleRole.trim(),
        logo: form.logo,
        banner: form.banner,
        cnpjCard: form.cnpjCard,
      }
    },

    resetForm() {
      this.revokePreviews()

      this.form = emptyForm()
      this.acceptCharges = false
      this.logoPreview = null
      this.bannerPreview = null
      this.rawLogo = null
      this.rawLogoDataUrl = null
      this.logoOptions = []
      this.selectedLogoOption = null
      this.bannerPhrase = ''
      this.bannerBg = 'solid'

      this.$refs.observer.reset()
    },
  },
}
</script>

<style scoped>
.color-picker {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.generator {
  border: 1px solid rgba(94, 86, 105, 0.14);
  border-radius: 0.5rem;
  background-color: rgba(94, 86, 105, 0.04);
}

.logo-option {
  width: 88px;
  height: 88px;
  border: 2px solid rgba(94, 86, 105, 0.22);
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
}

.logo-option--active {
  border-color: var(--v-primary-base);
}

/* No tema escuro as cores acima ficam abaixo do fundo do card e somem. */
.theme--dark .generator {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.04);
}

.theme--dark .logo-option {
  border-color: rgba(255, 255, 255, 0.2);
}

.logo-option img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.phone-preview {
  width: 280px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 1.75rem;
  overflow: hidden;
  background-color: #fff;
  color: #334155;
}

/* Preview simula tela clara: nao herdar branco/cinza claro do tema dark. */
.phone-preview .text-caption,
.phone-preview p,
.phone-preview span {
  color: inherit;
}

.phone-preview__muted {
  color: #64748b !important;
}

.phone-preview__icon--muted {
  color: #64748b !important;
}

.phone-preview__banner {
  position: relative;
  height: 88px;
  background-color: #dce3f7;
  margin-bottom: 8px;
}

.phone-preview__banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.phone-preview__logo {
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 4px solid #fff;
  background-color: #e2e8f0;
  overflow: hidden;
}

.phone-preview__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.phone-preview__bubble {
  background-color: #dce3f7;
  border-radius: 1rem;
  padding: 8px 12px;
  font-size: 0.75rem;
}
</style>
